import type { AutomationSchedule } from "@/lib/types";

/**
 * Convert an automation schedule to a human-readable string.
 */
export function describeSchedule(schedule: AutomationSchedule): string {
  const parts = schedule.cron.split(" ");
  if (parts.length !== 5) return schedule.cron;

  const [minute, hour, , , dow] = parts;

  // Interval patterns
  if (minute.startsWith("*/")) {
    return `Every ${minute.replace("*/", "")} minutes`;
  }
  if (hour.startsWith("*/")) {
    return `Every ${hour.replace("*/", "")} hours`;
  }

  // Daily patterns
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const timeStr = `${displayH}:${String(m).padStart(2, "0")} ${period}`;

  if (dow === "*") return `Daily at ${timeStr}`;
  if (dow === "1-5" || dow === "1,2,3,4,5") return `Weekdays at ${timeStr}`;
  if (dow === "0,6" || dow === "6,0") return `Weekends at ${timeStr}`;

  const dayNames: Record<string, string> = {
    "0": "Sun",
    "1": "Mon",
    "2": "Tue",
    "3": "Wed",
    "4": "Thu",
    "5": "Fri",
    "6": "Sat",
  };

  const daysList = dow
    .split(",")
    .map((d) => dayNames[d] ?? d)
    .join(", ");
  return `${daysList} at ${timeStr}`;
}

/**
 * Convert an ISO date string to a relative time string.
 */
export function relativeTime(isoStr: string): string {
  const date = new Date(isoStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Future dates
  if (diffMs < 0) {
    const absDiff = -diffMs;
    if (absDiff < 60000) return "in <1m";
    if (absDiff < 3600000) return `in ${Math.floor(absDiff / 60000)}m`;
    if (absDiff < 86400000) return `in ${Math.floor(absDiff / 3600000)}h`;
    return `in ${Math.floor(absDiff / 86400000)}d`;
  }

  // Past dates
  if (diffMs < 60000) return "<1m ago";
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return `${Math.floor(diffMs / 86400000)}d ago`;
}

/**
 * Format duration in milliseconds to human-readable.
 */
export function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return "—";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}
